import type { Club, RosterTotals } from "@/lib/api";
import { formatStat } from "@/lib/format";
import { sectionById } from "@/lib/sections";
import { useReveal } from "@/lib/useReveal";
import { SectionHeader } from "./SectionHeader";

interface CompeteSectionProps {
  readonly club: Club | null;
  readonly totals: RosterTotals | null;
}

export function CompeteSection({ club, totals }: CompeteSectionProps) {
  const ref = useReveal<HTMLDivElement>();
  const section = sectionById("compete")!;
  const league = club?.league;

  const figures = [
    {
      label: "Members on the board",
      value: totals ? String(totals.members) : "—",
    },
    { label: "Highest peak rank", value: totals?.top_peak_label ?? "—" },
    { label: "Median club PPS", value: formatStat(totals?.median_pps ?? null) },
  ];

  return (
    <section
      id="compete"
      className="band-deep border-b-2 border-ink"
    >
      <div className="shell py-16 sm:py-20 md:py-24">
        <SectionHeader
          section={section}
          kicker="Representing UCI in collegiate play."
        />

        <div
          ref={ref}
          className="reveal grid gap-8 lg:grid-cols-[1.1fr_0.9fr]"
          data-shown="false"
        >
          <div>
            <h3 className="text-[clamp(1.75rem,4.5vw,2.75rem)]">
              {league?.name ?? "Collegiate Tetris League"}
            </h3>
            <p className="mt-4 max-w-[52ch] text-lg leading-relaxed">
              Tetris At UCI fields a team in {league?.abbreviation ?? "CTL"},
              the intercollegiate circuit in which universities put their
              strongest players against one another. Matches are played online,
              and we watch and review them together as a club.
            </p>
            <p className="mt-4 max-w-[52ch] leading-relaxed opacity-80">
              You do not need a rank to attend a meeting. You do need one to
              play a league match, and we will help you get there.
            </p>
          </div>

          <dl className="grid gap-3 self-start">
            {figures.map((figure) => (
              <div
                key={figure.label}
                className="panel flex items-baseline justify-between gap-4 px-6 py-5"
              >
                <dt className="meta max-w-[16ch] leading-relaxed opacity-70">
                  {figure.label}
                </dt>
                <dd className="display text-[2.5rem] leading-none tabular-nums">
                  {figure.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
